import {
  ActionIcon,
  AppShell,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  Group,
  Header,
  Indicator,
  MantineProvider,
  Popover,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import { NotificationsProvider } from '@mantine/notifications';
import { IconBell, IconDoorExit, IconMenu, IconRefresh } from '@tabler/icons';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from './components/sidebar';
import {
  deleteReadNotificationsRequest,
  getNotificationsRequest,
  markNotificationsReadRequest,
} from './data/notifications';
import useAuthContext from './hooks/useAuthContext';
import { UserType } from './models/user';
import { Router } from './routes';
import { AppRoutePaths } from './utils/enums/routes';
import { AppNotification } from './utils/types/data/services/notification';

const NOTIFICATION_POLL_INTERVAL = 5 * 60 * 1000;

const NOTIFICATION_TYPE_LABELS: Record<
  string,
  { label: string; color: string }
> = {
  'lunch-late-return': { label: 'Atraso no retorno', color: 'orange' },
  'lunch-missed-punch': { label: 'Ponto não registrado', color: 'red' },
};

function notificationStyle(type: string) {
  return NOTIFICATION_TYPE_LABELS[type] ?? { label: type, color: 'gray' };
}

const PAGE_SIZE = 10;

function NotificationsWidget() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const isManager =
    user?.user.type === UserType.MANAGER || user?.user.type === UserType.MASTER;

  async function fetchNotifications(reset = false) {
    try {
      const nextOffset = reset ? 0 : offset;
      const { data } = await getNotificationsRequest({
        limit: PAGE_SIZE,
        offset: nextOffset,
      });
      setNotifications((prev) => (reset ? data : [...prev, ...data]));
      setOffset(nextOffset + data.length);
      setHasMore(data.length === PAGE_SIZE);
    } catch {
      // silently fail
    }
  }

  useEffect(() => {
    if (!isManager) return;
    fetchNotifications(true);
    const timer = setInterval(
      () => fetchNotifications(true),
      NOTIFICATION_POLL_INTERVAL,
    );
    return () => clearInterval(timer);
  }, [user]);

  if (!isManager) return null;

  async function handleOpen(open: boolean) {
    setPopoverOpen(open);
    if (open) {
      const unread = notifications.filter((n) => !n.read);
      if (unread.length > 0) {
        try {
          await markNotificationsReadRequest();
          setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        } catch {
          // silently fail
        }
      }
    }
  }

  async function handleLoadMore() {
    await fetchNotifications(false);
  }

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await fetchNotifications(true);
    } finally {
      setRefreshing(false);
    }
  }

  async function handleClearRead() {
    setClearing(true);
    try {
      await deleteReadNotificationsRequest();
      setNotifications((prev) => prev.filter((n) => !n.read));
    } catch {
      // silently fail
    } finally {
      setClearing(false);
    }
  }

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <Popover
      opened={popoverOpen}
      onChange={handleOpen}
      width={340}
      position="bottom-end"
      shadow="md"
      withinPortal
    >
      <Popover.Target>
        <Indicator
          label={unreadCount > 0 ? String(unreadCount) : undefined}
          size={16}
          color="red"
          disabled={unreadCount === 0}
        >
          <ActionIcon
            variant={popoverOpen ? 'filled' : 'subtle'}
            color="indigo"
            size="lg"
            onClick={() => handleOpen(!popoverOpen)}
          >
            <IconBell size={20} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>

      <Popover.Dropdown p={0}>
        <Group px={16} py={10} position="apart">
          <Text fw="bold" size="sm">
            Notificações
          </Text>
          <Group spacing={6}>
            <ActionIcon
              size="sm"
              variant="subtle"
              color="gray"
              loading={refreshing}
              onClick={handleRefresh}
              title="Atualizar"
            >
              <IconRefresh size={14} />
            </ActionIcon>
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              compact
              loading={clearing}
              onClick={handleClearRead}
            >
              Limpar lidas
            </Button>
            <Button
              size="xs"
              variant="subtle"
              compact
              onClick={() => {
                setPopoverOpen(false);
                navigate(AppRoutePaths.NOTIFICATIONS);
              }}
            >
              Ver todas
            </Button>
          </Group>
        </Group>
        <Divider />
        <ScrollArea.Autosize maxHeight={360}>
          {notifications.length === 0 ? (
            <Text size="sm" color="dimmed" p={16} align="center">
              Sem notificações no momento
            </Text>
          ) : (
            <Stack spacing={0}>
              {notifications.map((n) => {
                const { label, color } = notificationStyle(n.type);
                return (
                  <Box
                    key={n.id}
                    px={16}
                    py={10}
                    sx={(theme) => ({
                      borderBottom: `1px solid ${theme.colors.gray[2]}`,
                      backgroundColor: n.read
                        ? undefined
                        : theme.colors.blue[0],
                    })}
                  >
                    <Group spacing={6} mb={2}>
                      <Badge color={color} size="xs">
                        {label}
                      </Badge>
                    </Group>
                    <Text size="xs" fw={500}>
                      {n.title}
                    </Text>
                    <Text size="xs" color="dimmed">
                      {n.message}
                    </Text>
                  </Box>
                );
              })}
              {hasMore && (
                <Box px={16} py={10}>
                  <Button
                    size="xs"
                    variant="subtle"
                    fullWidth
                    onClick={handleLoadMore}
                  >
                    Carregar mais
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
}

function App() {
  const { user, logout } = useAuthContext();
  const [drawerOpened, setDrawerOpened] = useState(false);

  useEffect(() => {
    if (!user) {
      logout();
    }
  }, [user]);

  return (
    <>
      <MantineProvider
        withGlobalStyles
        withNormalizeCSS
        theme={{
          fontFamily: 'Roboto, sans-serif',
          headings: {
            fontFamily: 'Roboto, sans-serif',
          },
          colorScheme: 'light',
          colors: {
            ltpBlue: [
              '#EAF0FB',
              '#C4D5F3',
              '#9EBAEB',
              '#789FE3',
              '#5285DB',
              '#2C6AD3',
              '#2355A9',
              '#1A3F7F',
              '#112A55',
              '#09152A',
            ],
          },
        }}
      >
        <AppShell
          navbarOffsetBreakpoint="sm"
          asideOffsetBreakpoint="sm"
          styles={() => ({
            main: {
              backgroundColor: '#FBFBFB',
            },
          })}
          header={
            <Header hidden={!user} height={{ base: 50, md: 50 }} p={0}>
              <Group
                style={{ height: '100%' }}
                px={20}
                position={user?.user.type === UserType.USER ? 'right' : 'apart'}
              >
                {user && (
                  <>
                    {user.user.type !== UserType.USER && (
                      <Button
                        onClick={() => setDrawerOpened(true)}
                        variant="subtle"
                        color="indigo"
                      >
                        <IconMenu size={16} />
                      </Button>
                    )}
                    <Group spacing={8}>
                      <NotificationsWidget />
                      <Button
                        color="indigo"
                        leftIcon={<IconDoorExit size={16} />}
                        onClick={() => logout()}
                        variant="light"
                      >
                        Sair
                      </Button>
                    </Group>
                  </>
                )}
              </Group>
            </Header>
          }
        >
          <NotificationsProvider position="top-right">
            <Drawer
              opened={drawerOpened}
              onClose={() => setDrawerOpened(false)}
              styles={{ header: { padding: 8 } }}
              title={<Text fw="bold">Menu</Text>}
            >
              <Sidebar setOpened={setDrawerOpened} />
            </Drawer>
            <Router />
          </NotificationsProvider>
        </AppShell>
      </MantineProvider>
    </>
  );
}

export default App;
